import React, { useState, useEffect } from 'react';
import { Upload, Save, AlertCircle, CheckCircle, Code, Terminal, Cloud, X, Snowflake, Server, AlertTriangle, Link2 } from 'lucide-react';
import Snowfall from 'react-snowfall';

interface TemplateSettings {
  customScript: string;
  customClass: string;
  templateMode: 'files' | 'links';
  templateLinkUrl: string;
}

interface DomainCloudflare {
  id: number;
  domain: string;
  cf_email?: string;
  cf_api_token?: string;
  cloudapi_email?: string;
  cloudapi_api_key?: string;
}

const SettingsPanel: React.FC = () => {
  const [settings, setSettings] = useState<TemplateSettings>({ customScript: '', customClass: '', templateMode: 'files', templateLinkUrl: '' });
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showConsole, setShowConsole] = useState(false);
  const [consoleData, setConsoleData] = useState<{ files: string[]; script: string; class: string; templateMode: string; templateLinkUrl: string } | null>(null);
  const [animatedText, setAnimatedText] = useState('');
  const [domainsList, setDomainsList] = useState<DomainCloudflare[]>([]);
  const [snowEnabled, setSnowEnabled] = useState(() => {
    const saved = localStorage.getItem('snowEnabled');
    return saved === 'true';
  });
  const [serverCleanupEnabled, setServerCleanupEnabled] = useState(false);
  const [isLoadingCleanup, setIsLoadingCleanup] = useState(false);

  // Safety Limits
  const [safetyLimitEnabled, setSafetyLimitEnabled] = useState(true);
  const [showSafetyModal, setShowSafetyModal] = useState(false);
  const [isLoadingSafety, setIsLoadingSafety] = useState(false);

  // Save snow state to localStorage
  useEffect(() => {
    localStorage.setItem('snowEnabled', snowEnabled.toString());
  }, [snowEnabled]);

  useEffect(() => { loadSettings(); loadDomainsList(); loadCleanupSettings(); loadSafetySettings(); }, []);

  const loadSettings = async () => {
    try {
      const res = await fetch('/api/settings/template', { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setSettings({
          customScript: data.settings?.customScript || '',
          customClass: data.settings?.customClass || '',
          templateMode: data.settings?.templateMode === 'links' ? 'links' : 'files',
          templateLinkUrl: data.settings?.templateLinkUrl || ''
        });
      }
    } catch { }
  };

  const loadDomainsList = async () => {
    try {
      const res = await fetch('/api/settings/domains-credentials', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setDomainsList(data.domains);
    } catch { }
  };

  const loadCleanupSettings = async () => {
    try {
      const res = await fetch('/api/settings/server-cleanup', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setServerCleanupEnabled(data.enabled);
    } catch { }
  };

  const toggleServerCleanup = async () => {
    setIsLoadingCleanup(true);
    try {
      const res = await fetch('/api/settings/server-cleanup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !serverCleanupEnabled })
      });
      const data = await res.json();
      if (data.success) {
        setServerCleanupEnabled(!serverCleanupEnabled);
        setMessage({ type: 'success', text: `Server cleanup ${!serverCleanupEnabled ? 'enabled' : 'disabled'}` });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to update cleanup settings' });
    } finally {
      setIsLoadingCleanup(false);
    }
  };

  const loadConsoleData = async () => {
    try {
      const res = await fetch('/api/settings/template/console', { credentials: 'include' });
      const data = await res.json();
      if (data.success) {
        setConsoleData(data.data);
        setShowConsole(true);
        setAnimatedText('');
        animateConsole(data.data);
      }
    } catch { }
  };

  const loadSafetySettings = async () => {
    try {
      const res = await fetch('/api/settings/safety-limit', { credentials: 'include' });
      const data = await res.json();
      if (data.success) setSafetyLimitEnabled(data.enabled);
    } catch { }
  };

  const toggleSafetyLimit = async () => {
    setIsLoadingSafety(true);
    try {
      const res = await fetch('/api/settings/safety-limit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enabled: !safetyLimitEnabled })
      });
      const data = await res.json();
      if (data.success) {
        setSafetyLimitEnabled(!safetyLimitEnabled);
        setMessage({ type: 'success', text: `Safety limits ${!safetyLimitEnabled ? 'enabled' : 'disabled'}` });
      }
    } catch {
      setMessage({ type: 'error', text: 'Failed to update safety settings' });
    } finally {
      setIsLoadingSafety(false);
    }
  };

  const animateConsole = (data: { files: string[]; script: string; class: string; templateMode: string; templateLinkUrl: string }) => {
    let output = `Script: ${data.script || '<empty>'}\n\nClass: ${data.class || '<empty>'}\n\nMode: ${data.templateMode || 'files'}\n\nLink: ${data.templateLinkUrl || '<empty>'}\n\nFiles:\n`;
    output += data.files.length > 0 ? data.files.join('\n') : '(no files)';
    let i = 0;
    const interval = setInterval(() => {
      if (i < output.length) { setAnimatedText(output.substring(0, i + 1)); i++; }
      else clearInterval(interval);
    }, 10);
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('customScript', settings.customScript);
      formData.append('customClass', settings.customClass);
      formData.append('templateMode', settings.templateMode);
      formData.append('templateLinkUrl', settings.templateLinkUrl);
      if (zipFile) formData.append('templateZip', zipFile);

      const res = await fetch('/api/settings/template', { method: 'POST', credentials: 'include', body: formData });
      const data = await res.json();

      if (data.success) {
        setMessage({ type: 'success', text: 'Settings saved!' });
        setZipFile(null);
        const input = document.getElementById('zipInput') as HTMLInputElement;
        if (input) input.value = '';
      } else setMessage({ type: 'error', text: data.error || 'Save failed' });
    } catch { setMessage({ type: 'error', text: 'Failed to save' }); }
    finally { setLoading(false); }
  };

  return (
    <div className="w-full min-h-full animate-fade-in">
      {/* Snowfall Effect */}
      {snowEnabled && (
        <Snowfall
          style={{
            position: 'fixed',
            width: '100vw',
            height: '100vh',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
          snowflakeCount={50}
          speed={[0.5, 1.5]}
          wind={[-0.5, 1]}
          radius={[0.5, 2]}
        />
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-12">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Settings</h1>
          <p className="text-white/50">Template and API configuration</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              onClick={() => setShowSafetyModal(!showSafetyModal)}
              className={`p-2 rounded-lg transition-colors ${safetyLimitEnabled ? 'text-orange-400 bg-orange-500/10' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
              title="Safety Limits"
            >
              <AlertTriangle className="w-4 h-4" />
            </button>
            {/* Safety Limit Modal/Popover */}
            {showSafetyModal && (
              <div className="absolute right-0 top-12 w-80 bg-[#111111] border border-[#2f3336] rounded-xl shadow-2xl z-50 p-4 animate-scale-in">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-orange-400" />
                    Safety Limits
                  </span>
                  <button onClick={() => setShowSafetyModal(false)}><X className="w-4 h-4 text-white/40 hover:text-white" /></button>
                </div>
                <p className="text-sm text-white/60 mb-4 leading-relaxed">
                  On Moscow servers, limits are applied to prevent bans:
                  <ul className="list-disc pl-4 mt-2 space-y-1">
                    <li>Max 3 activations per SteamID</li>
                    <li>Max 5 activations per Moscow server</li>
                  </ul>
                </p>
                <div className="flex items-center justify-between bg-white/5 p-3 rounded-lg">
                  <span className="text-sm text-white">Enable Limits</span>
                  <button
                    onClick={toggleSafetyLimit}
                    disabled={isLoadingSafety}
                    className={`relative w-10 h-5 rounded-full transition-colors ${safetyLimitEnabled ? 'bg-orange-500' : 'bg-white/10'}`}
                  >
                    <div className={`absolute top-1 w-3 h-3 rounded-full bg-white transition-all ${safetyLimitEnabled ? 'left-6' : 'left-1'}`} />
                  </button>
                </div>
              </div>
            )}
          </div>
          <button
            onClick={() => setSnowEnabled(!snowEnabled)}
            className={`p-2 rounded-lg transition-colors ${snowEnabled ? 'text-cyan-400 bg-cyan-500/10' : 'text-white/40 hover:text-white hover:bg-white/5'}`}
            title={snowEnabled ? 'Stop snow' : 'Let it snow!'}
          >
            <Snowflake className="w-4 h-4" />
          </button>
          <button onClick={loadConsoleData} className="flex items-center gap-2 px-4 py-2 text-white/70 hover:text-white transition-colors">
            <Terminal className="w-4 h-4" />
            <span className="text-sm">Console</span>
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${message.type === 'success' ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
          {message.type === 'success' ? <CheckCircle className="w-5 h-5 text-green-400" /> : <AlertCircle className="w-5 h-5 text-red-400" />}
          <span className={message.type === 'success' ? 'text-green-400' : 'text-red-400'}>{message.text}</span>
        </div>
      )}

      <div className="space-y-6">
        {/* Cloudflare */}
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Cloud className="w-5 h-5 text-orange-400" />
              <div>
                <span className="text-white font-medium">Cloudflare API</span>
                <p className="text-sm text-white/40">{domainsList.length} domains configured</p>
              </div>
            </div>
            <a
              href="/domains"
              className="px-4 py-2 text-sm text-white bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors"
            >
              Manage in Domains →
            </a>
          </div>

          {domainsList.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {domainsList.slice(0, 5).map((d) => (
                <span key={d.id} className="px-2 py-1 text-xs bg-white/5 text-white/60 rounded">
                  {d.domain}
                </span>
              ))}
              {domainsList.length > 5 && (
                <span className="px-2 py-1 text-xs text-white/40">+{domainsList.length - 5} more</span>
              )}
            </div>
          )}
        </div>

        {/* Server Cleanup */}
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-blue-400" />
              <div>
                <span className="text-white font-medium">Auto Server Cleanup</span>
                <p className="text-sm text-white/40">Automatically removes orphaned files every 24h</p>
              </div>
            </div>
            <button
              onClick={toggleServerCleanup}
              disabled={isLoadingCleanup}
              className={`relative w-12 h-6 rounded-full transition-colors ${serverCleanupEnabled ? 'bg-green-500' : 'bg-white/10'
                }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${serverCleanupEnabled ? 'left-7' : 'left-1'
                }`} />
            </button>
          </div>
          <p className="text-xs text-white/30 mt-3">
            Scans linked servers for files without active pages and removes them.
            Notifications will be sent when cleanup is performed.
          </p>
        </div>

        {/* Template */}
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-5 space-y-5">
          <div className="flex items-center gap-3">
            <Code className="w-5 h-5 text-white/40" />
            <span className="text-white font-medium">Template Settings</span>
          </div>

          <div>
            <label className="block text-sm text-white/50 mb-2">Custom Script Tag</label>
            <textarea
              value={settings.customScript}
              onChange={(e) => setSettings({ ...settings, customScript: e.target.value })}
              placeholder='<script src="/script.js"></script>'
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-white/30 resize-none"
              rows={3}
            />
            <p className="text-xs text-white/30 mt-1">Added to all pages</p>
          </div>

          <div>
            <label className="block text-sm text-white/50 mb-2">Custom CSS Class</label>
            <input
              type="text"
              value={settings.customClass}
              onChange={(e) => setSettings({ ...settings, customClass: e.target.value })}
              placeholder="custom-class"
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-white/30"
            />
            <p className="text-xs text-white/30 mt-1">Added to banner elements</p>
          </div>

          <div className="space-y-3">
            <label className="block text-sm text-white/50">Template Source</label>
            <div className="flex items-center gap-2">
              {(['files', 'links'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSettings({ ...settings, templateMode: mode })}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${settings.templateMode === mode ? 'bg-white text-black border-white' : 'bg-white/5 text-white/70 border-white/10 hover:border-white/20'}`}
                >
                  {mode}
                </button>
              ))}
            </div>
            {settings.templateMode === 'links' && (
              <div>
                <label className="block text-sm text-white/50 mb-2">Iframe Link</label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    type="url"
                    value={settings.templateLinkUrl}
                    onChange={(e) => setSettings({ ...settings, templateLinkUrl: e.target.value })}
                    placeholder="https://example.com/ad"
                    className="w-full pl-10 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-white/30"
                  />
                </div>
                <p className="text-xs text-white/30 mt-1">Used as windowAd iframe source on activation</p>
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm text-white/50 mb-2">Template Assets (ZIP)</label>
            <div className="flex items-center gap-3">
              <input id="zipInput" type="file" accept=".zip" onChange={(e) => e.target.files?.[0] && setZipFile(e.target.files[0])} className="hidden" />
              <label htmlFor="zipInput" className="px-4 py-2 text-sm text-white/70 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors cursor-pointer flex items-center gap-2">
                <Upload className="w-4 h-4" />
                {zipFile ? zipFile.name : 'Choose file'}
              </label>
              {zipFile && <span className="text-sm text-green-400">{(zipFile.size / 1024).toFixed(1)} KB</span>}
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button onClick={handleSaveSettings} disabled={loading} className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50">
              {loading ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Language Settings */}
        <div className="rounded-lg bg-white/[0.02] border border-white/10 p-5 relative overflow-hidden">
          {/* Soon Overlay */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px] flex items-center justify-center z-10">
            <span className="text-white/60 font-medium text-sm">Soon</span>
          </div>
          <h3 className="text-white font-medium mb-4">Language Settings</h3>
          <div className="space-y-3">
            <div>
              <label className="block text-sm text-white/50 mb-2">Interface Language</label>
              <select
                className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-white/30"
                defaultValue="en"
                disabled
              >
                <option value="en">English</option>
                <option value="ru">Русский</option>
                <option value="ua">Українська</option>
                <option value="zh">中文</option>
                <option value="es">Español</option>
                <option value="de">Deutsch</option>
              </select>
              <p className="text-xs text-white/30 mt-2">Changes will apply after page reload</p>
            </div>
          </div>
        </div>
      </div>

      {/* Console Modal */}
      {showConsole && consoleData && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={() => setShowConsole(false)}>
          <div className="bg-black border border-green-500/30 rounded-xl p-5 max-w-4xl w-full max-h-[80vh] overflow-auto font-mono" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/10">
              <div className="flex items-center gap-2 text-green-400">
                <Terminal className="w-5 h-5" />
                <span>ikcore@console:~$</span>
              </div>
              <button onClick={() => setShowConsole(false)} className="p-1 hover:bg-white/5 rounded"><X className="w-5 h-5 text-white/40" /></button>
            </div>
            <div className="text-sm text-white/70 whitespace-pre-wrap">{animatedText}<span className="animate-pulse">_</span></div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPanel;
